import { Link, type MetaFunction } from 'react-router'

import { Icon } from '#app/components/ui/icon.tsx'
import { cn } from '#app/utils/cn.ts'
const plans = [
	{
		name: 'Free',
		price: '$0',
		status: 'Available now',
		description:
			'For basic performance monitoring, secure by default with MFA, passkeys & SSO.',
		features: [
			'50 audits / month',
			'Audit scheduling',
			'Single region (US)',
			'SSO with Google & Github',
			'30 day report retention',
		],
		cta: 'signup',
		note: null,
	},
	{
		name: 'Pro',
		price: '$15 / month',
		status: 'Coming soon',
		description:
			'For individuals who want continuous monitoring and automation.',
		features: [
			'1,000 audits / month',
			'Authenticated audits',
			'22 regions worldwide',
			'API and webhooks',
			'2 year report retention',
		],
		cta: 'waitlist',
	},
	{
		name: 'Team',
		price: '$39 / month',
		status: 'Coming soon',
		description: 'For teams monitoring multiple pages at scale.',
		features: [
			'5,000 audits / month',
			'Team & access management',
			'Unlimited seats*',
			'Unlimited pages*',
			'2 year report retention',
		],
		cta: 'waitlist',
	},
]

export const meta: MetaFunction = () => [
	{ title: 'Pricing | xcelera' },
	{
		name: 'description',
		content: 'Simple usage-focused pricing for xcelera plans.',
	},
]

export default function PricingRoute() {
	return (
		<main className="bg-background relative min-h-screen overflow-hidden">
			<div className="from-muted/50 via-background to-background absolute inset-0 bg-linear-to-b dark:from-neutral-950 dark:via-neutral-950 dark:to-neutral-900" />
			<div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top,var(--tw-gradient-stops))] from-black/5 via-transparent to-transparent opacity-20 dark:bg-[radial-gradient(circle_at_top,rgba(255,255,255,0.08),transparent_55%)] dark:opacity-40" />

			<section className="relative mx-auto flex max-w-6xl flex-col items-center gap-4 px-4 py-16 sm:py-24 xl:px-6">
				<div className="mx-auto max-w-3xl text-center">
					<h1 className="text-foreground text-3xl font-bold tracking-tight sm:text-4xl">
						Pricing
					</h1>
					<p className="text-muted-foreground mt-4 text-base leading-relaxed sm:text-lg">
						Simple, usage-focused pricing. Start free, upgrade when you need
						more.
					</p>
				</div>

				<div className="mt-10 grid gap-4 md:grid-cols-3 md:grid-rows-[repeat(5,auto)]">
					{plans.map((plan) => (
						<div
							key={plan.name}
							className={cn(
								'relative grid w-full grid-rows-[auto_auto_auto_1fr_auto] rounded-2xl border p-6 shadow-sm backdrop-blur',
								'border-border bg-card/50 dark:border-white/10 dark:bg-neutral-950/60 dark:shadow-[0_20px_80px_-60px_rgba(0,0,0,0.8)]',
								'md:row-span-5 md:grid-rows-subgrid',
							)}
						>
							<div className="flex items-center justify-between">
								<h2 className="text-foreground text-lg font-semibold">
									{plan.name}
								</h2>
								<span className="text-muted-foreground text-xs tracking-[0.2em] uppercase dark:text-white/50">
									{plan.status}
								</span>
							</div>
							<p className="text-foreground mt-5 text-3xl font-semibold">
								{plan.price}
							</p>
							<p className="text-muted-foreground mt-2 text-sm">
								{plan.description}
							</p>

							<ul className="text-muted-foreground mt-2 flex flex-col gap-3 text-sm dark:text-white/80">
								{plan.features.map((feature) => (
									<li key={feature} className="flex items-start gap-2">
										<Icon
											name="check"
											className="mt-0.5 text-emerald-600 dark:text-emerald-300"
										/>
										<span>{feature}</span>
									</li>
								))}
							</ul>

							{plan.cta === 'waitlist' ? (
								<WaitlistCtaButton />
							) : (
								<SignupCtaButton />
							)}
						</div>
					))}
				</div>

				<div className="text-muted-foreground mx-auto mt-4 max-w-4xl text-center text-sm dark:text-white/50">
					<p>
						* Pricing is primarily based on usage, not seats or number of pages
						monitored (within reasonable limits).
					</p>
					<p>
						* Pro plan includes everything in Free, Team plan includes
						everything in Pro.
					</p>
				</div>
			</section>
		</main>
	)
}

function SignupCtaButton() {
	return (
		<Link
			to="/signup"
			className={cn(
				pricingCtaBase,
				'border-input hover:bg-accent hover:text-accent-foreground border dark:border-white/20 dark:text-white dark:hover:bg-white/10',
			)}
		>
			Get started
		</Link>
	)
}

function WaitlistCtaButton() {
	return (
		<Link
			to="/waitlist"
			className={cn(
				pricingCtaBase,
				'bg-emerald-600 text-white hover:bg-emerald-700 dark:bg-emerald-300 dark:text-neutral-950 dark:hover:bg-emerald-200',
			)}
		>
			Join the waitlist
		</Link>
	)
}

const pricingCtaBase =
	'mt-8 inline-flex w-full items-center justify-center rounded-lg px-4 py-2 text-sm font-semibold transition'
